import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { IconComponent } from '../../atoms/icon/icon.component';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-button',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    LinkComponent,
    IconComponent,
    CapitalizePipe,
  ],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class ButtonComponent {
  @Input() type: ButtonType = ButtonType.PRIMARY;
  @Input() label: string = '';
  @Input() target?: string;
  @Input() href?: string;
  @Input() fragment?: string = '';
  @Input() hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() linkRel?: string = 'follow';
  @Input() linkTitle: string = '';
  @Input() disabled?: boolean = false;
  @Input() bold: boolean = true;
  @Input() iconName?: string;
  @Input() iconPrefix?: string;
  @Input() iconLeftSelected?: string[];
  @Input() isNavigationIcon?: boolean = false;
  @Input() outline?: boolean;
  @Input() outlineColor?: Colors = Colors.BLACK;
  @Input() click: () => void = () => {};
  @Input() expandButton: boolean = false;
  @Input() shrinkButton: boolean = false;
  @Input() sizes: Sizes = Sizes.MEDIUM;
  @Input() targetArray?: string[];
  @Input() isWithoutIcon?: boolean = false;
  @Output() clickEvent = new EventEmitter<Event>();
  @Input() isOpen = false;

  Sizes = Sizes;
  HrefType = HrefType;
  LinkType = LinkType;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  lefties = [
    ButtonType.DOUBLE_ICON,
    ButtonType.SECONDARY_ICON,
    ButtonType.LOGIN,
    ButtonType.EXIT,
    ButtonType.MENU,
    ButtonType.BACK_TO_TOP,
    ButtonType.OPEN_CLOSE,
    ButtonType.OPEN_CLOSE_SECONDARY,
    ButtonType.SQUARED_PRIMARY,
    ButtonType.SQUARED_SECONDARY,
    ButtonType.ROUNDED_PRIMARY,
    ButtonType.ROUNDED_SECONDARY,
    ButtonType.ROUNDED_TERTIARY,
    ButtonType.SEARCH,
    ButtonType.SEARCH_NO_BG,
  ];

  iconsOnly = [
    ButtonType.BACK_TO_TOP,
    ButtonType.OPEN_CLOSE,
    ButtonType.OPEN_CLOSE_SECONDARY,
    ButtonType.SQUARED_PRIMARY,
    ButtonType.SQUARED_SECONDARY,
    ButtonType.ROUNDED_PRIMARY,
    ButtonType.ROUNDED_SECONDARY,
    ButtonType.ROUNDED_TERTIARY,
    ButtonType.SEARCH,
    ButtonType.SEARCH_NO_BG,
  ];

  getIconName = (isLeftIcon: boolean = false): string => {
    if (this.iconName) {
      return this.iconName;
    }
    if (isLeftIcon && this.shouldDisplayLeftIcon()) {
      if (this.type === ButtonType.DOUBLE_ICON || this.iconLeftSelected) {
        return this.iconLeftSelected?.[1] || '';
      }
    }
    const iconType = this.getTypeIcon();
    return iconType || '';
  };

  getIconPrefix = (): string => {
    return this.iconPrefix ? this.iconPrefix : 'fal';
  };

  public getTypeIcon = (): string => {
    if (ButtonType.DOUBLE_ICON === this.type && this.iconName) {
      return this.iconName;
    }
    switch (this.type) {
      case ButtonType.PRIMARY_ICON:
        return 'arrow-right-long';
      case ButtonType.SECONDARY_ICON:
        return 'plus';
      case ButtonType.BACK_TO_TOP:
        return 'chevron-up';
      case ButtonType.OPEN_CLOSE:
      case ButtonType.OPEN_CLOSE_SECONDARY:
        return 'plus';
      case ButtonType.ROUNDED_PRIMARY:
      case ButtonType.ROUNDED_SECONDARY:
      case ButtonType.ROUNDED_TERTIARY:
        return 'play';
      case ButtonType.SEARCH:
      case ButtonType.SEARCH_NO_BG:
        return 'search';
      case ButtonType.LOGIN:
        return 'arrow-right-to-bracket';
      case ButtonType.MENU:
        return 'bars';
      case ButtonType.DROPDOWN:
        return 'chevron-down';
      case ButtonType.EXIT:
        return 'x';
      default:
        return '';
    }
  };

  shouldDisplayLeftIcon(): boolean {
    return this.lefties.includes(this.type);
  }

  shouldDisplayLabel(): boolean {
    return !this.iconsOnly.includes(this.type);
  }

  shouldDisplayRightIcon(): boolean {
    return this.type === ButtonType.DROPDOWN;
  }

  toggleOpenClose(): void {
    this.isOpen = !this.isOpen;
  }

  handleClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    this.clickEvent.emit(event);
  }

  getAriaLabel(): string {
    if (this.label) return this.label;

    switch (this.type) {
      case ButtonType.BACK_TO_TOP:
        return 'Zurück nach oben';
      case ButtonType.SEARCH:
      case ButtonType.SEARCH_NO_BG:
        return 'Suche';
      case ButtonType.OPEN_CLOSE:
      case ButtonType.OPEN_CLOSE_SECONDARY:
        return this.isOpen ? 'Schließen' : 'Öffnen';
      case ButtonType.LOGIN:
        return 'Login';
      case ButtonType.EXIT:
        return 'Logout';
      default:
        return 'Aktion ausführen';
    }
  }

  protected readonly ButtonType = ButtonType;
}
